import React, { useState, useEffect, useRef } from 'react';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import styles from './Checkout.module.scss'
import { walletGoesFirst, walletSheetAmounts } from '../../../utils/walletPayment';
import { trackPaymentFailure } from '../../../utils/trackFunnel';

// The Apple Pay / Google Pay / Link payment request, owned by Checkout so it can
// place the button: first in the list for Apple Pay and Google Pay, below PayPal
// for Link (walletGoesFirst). Returns { paymentRequest, goesFirst }; paymentRequest
// stays null until Stripe says the device can pay.
export const useWalletPaymentRequest = ({ cart, setValue, setShowShipping, usStates, setOrderErrors, skipShipping }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [goesFirst, setGoesFirst] = useState(false);

  // The 'paymentmethod' handler is registered once, so it reads the latest
  // props through a ref rather than the ones it was created with.
  const latest = useRef();
  latest.current = { cart, setValue, setShowShipping, usStates, setOrderErrors, skipShipping };

  const setShippingInfo = (ev) => {
    const { setValue: set, setShowShipping: show, usStates: states, skipShipping: skip } = latest.current;
    set("payment_method", "stripe");
    show(true);

    if (skip || !ev.shippingAddress) return;

    const state = states.find(s => s.value === ev.shippingAddress.region);
    set('shipping_address', ev.shippingAddress.addressLine[0])
    set('shipping_address2', ev.shippingAddress.addressLine[1])
    set('shipping_state', state)
    set('shipping_city', ev.shippingAddress.city)
    set('shipping_zip', ev.shippingAddress.postalCode)
  }

  const hasCart = !!cart;

  useEffect(() => {
    if (!stripe || !hasCart) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: !skipShipping,
      ...walletSheetAmounts(latest.current.cart, skipShipping),
    });

    // Check the availability of the Payment Request API.
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setGoesFirst(walletGoesFirst(result));
      }
    });

    pr.on('paymentmethod', async (e) => {
      const { client_secret, error } = await fetch(
        `/api/v1/carts/${latest.current.cart.id}/create_payment_intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentMethodType: 'card',
            currency: 'usd',
          }),
        }
      ).then((response) => response.json())

      if (error) {
        trackPaymentFailure('stripe_wallet', {
          cartId: latest.current.cart?.id, stage: 'create_payment_intent', error,
        });
        latest.current.setOrderErrors([e.message || 'There was an error processing your payment. Please try again or use another payment method.'])
        e.complete('fail');
        return;
      }

      e.complete('success');

      setShippingInfo(e);
      window.stripeOrder = e;
      window.stripeKey = client_secret;
    });
  }, [stripe, hasCart]);

  // Keep the sheet's amount on the current cart (a coupon removed on this page
  // changes the total). Stripe refuses an update while the sheet is open.
  const apiTotal = cart && cart.apiTotal;
  const shipping = cart && cart.shipping;
  useEffect(() => {
    if (!paymentRequest || !cart || paymentRequest.isShowing()) return;

    paymentRequest.update(walletSheetAmounts(cart, skipShipping));
  }, [paymentRequest, apiTotal, shipping]);

  return { paymentRequest, goesFirst };
}

const Stripe = ({ paymentRequest, unregister, register }) => {
  if (!paymentRequest) return null;

  return (
    <div className={styles.radioGroup}>
      <label className={styles.paymentLabel}>
        <input
          {...register("payment_method", { required: true })}
          type="radio"
          onChange={() => unregister(
            ["name_on_card", "card_number", "card_expires_on", "card_verification",
              "address", "address2", "city", "state", "zip_code", "payment_method"])}
          value="stripe" />
          <span>
            <PaymentRequestButtonElement options={{paymentRequest}} />
          </span>
      </label>
    </div>
  );
}

export default Stripe;
